import { lazy } from 'react';
import { IconChecklist } from '@tabler/icons-react';

import { TaskDetailContent } from '@/components/tasks/TaskDetailsPanel/TaskDetailContent';
import { selectHasActiveTaskFilters } from '@/store/selectors/taskFilterSelectors';
import { selectArtifactTasksVisible } from '@/store/selectors/layerSelectors';
import type { GeoMarkupStore } from '@/store';
import { VIEW_MODE, type ViewMode } from '@/types';

import { TaskFormPanel } from './TaskFormPanel';
import type { ArtifactDefinition } from './registry';

/**
 * `BackgroundPage` is lazy-loaded to break the potential import cycle
 * `registry → tasks → TasksPage → hooks → registry`. Keeping the page
 * behind `React.lazy` also trims the initial bundle.
 */
const TasksPage = lazy(() =>
  import('@/pages/TasksPage/TasksPage').then((module) => ({
    default: module.TasksPage,
  }))
);

/** Lazy-loaded for the same cycle-breaking reason as `NotesPanelContent`. */
const TasksPanelContent = lazy(() =>
  import('@/components/ui/SidePanel/TasksPanelContent').then((module) => ({
    default: module.TasksPanelContent,
  }))
);

export const tasksArtifact: ArtifactDefinition = {
  id: 'tasks',
  singular: 'task',
  icon: IconChecklist,
  labelKey: 'appShell.sidebar.tasks',
  supportedViews: [VIEW_MODE.MAP, VIEW_MODE.LIST, VIEW_MODE.KANBAN] as const,
  defaultView: VIEW_MODE.MAP,
  BackgroundPage: TasksPage,
  PanelContent: TasksPanelContent,
  DetailContent: TaskDetailContent,
  FormPanel: TaskFormPanel,
  getSlice: (state) => state.tasks,
  selectors: {
    hasActiveFilters: selectHasActiveTaskFilters,
    isLayerVisible: selectArtifactTasksVisible,
  },
  syncViewMode: (view: ViewMode, store: GeoMarkupStore): void => {
    if (store.ui.taskViewMode !== view) {
      store.ui.setTaskViewMode(view);
    }
  },
};
